import React, { useContext, useState } from 'react'
import * as API from '../../util/API'
import * as GTM from '../../util/GTM'
import { PhoneNumberData } from 'phone-number'
import LeadContext from '../../LeadContext'
import { useHistory } from 'react-router-dom'
import { getCookieValue } from '../../util/Cookies'
import NumberReservationContext from 'number-reservation-context/NumberReservationContext'

interface ReserveNumberContextProviderProps {
    children: JSX.Element
}

/**
 * This context provides components throughout the pick-a-number step with access to state related to
 * reserving a number in order to purchase it.
 * This includes the status of the request, functions to reserve a number, and in the future numbers that
 * were found to be already taken.
 */
const ReserveNumberContextProvider = ({ children } : ReserveNumberContextProviderProps) : JSX.Element => {
    const [isReservingNumber, setIsReservingNumber] = useState<boolean>(false)
    const [numberBeingReserved, setNumberBeingReserved] = useState<PhoneNumberData | null>(null)
    const [numbersTaken, setNumbersTaken] = useState<PhoneNumberData[]>([])
    const [error, setError] = useState<Error | null>(null)

    const { addToCart } = useContext(LeadContext)

    const history = useHistory()

    const reserveNumber = async (phoneNumber: PhoneNumberData) => {
        try {
            setIsReservingNumber(true)
            setNumberBeingReserved(phoneNumber)

            const response = await API.reserveNumber(phoneNumber)
            const { data } = response
            addToCart({
                e164: data.number_e164,
                mask: data.number_mask,
                status: 'number_available'
            })
            history.push(`/checkout?session_token=${getCookieValue('pdc_lead_session_token')}`)

            setIsReservingNumber(false)
            setNumberBeingReserved(null)
        } catch (error) {
            console.error(`Error reserving number: ${phoneNumber.e164}: ${error}`)

            if (error.message === 'number_not_available') {
                GTM.numberAlreadyTaken(phoneNumber.e164)
                setNumbersTaken([...numbersTaken, phoneNumber])
            } else {
                GTM.reserveNumberError(error.message)
            }

            const defaultErrorMessage = 'Something went wrong, please try again or contact our sales team.'
            const userErrorMessages = {
                number_not_available: 'It looks like someone got to that number first. Please select a different number.' // eslint-disable-line @typescript-eslint/naming-convention
            }

            const userFacingError = new Error(userErrorMessages[error.message] || defaultErrorMessage)
            setError(userFacingError)
        }
    }

    const clearReservationError = () => {
        setError(null)
    }

    return (
        <NumberReservationContext.Provider
            value={{
                reserveNumber,
                isReservingNumber,
                numberBeingReserved,
                numbersTaken,
                error,
                clearReservationError,
                reservedNumbers: []
            }}
        >
            {children}
        </NumberReservationContext.Provider>
    )
}

export default ReserveNumberContextProvider
